import {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  TouchEvent as ReactTouchEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from './cn';
import { Check, Chevron } from './icons';

export type MenuItem = {
  label: string;
  icon?: ReactNode;
  /** Right-aligned hint (e.g. a shortcut). Ignored when `checked` or a submenu is present. */
  shortcut?: string;
  /** Show a trailing check (e.g. the current status in a "Set status" submenu). */
  checked?: boolean;
  danger?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  submenu?: MenuEntry[];
};
/** A non-interactive block of custom content (e.g. an info/metadata footer). Not focusable, skipped by
 *  keyboard roving. */
export type MenuNode = { node: ReactNode };
/** A row, a custom node, or a horizontal divider between groups. */
export type MenuEntry = MenuItem | MenuNode | 'separator';

const LONG_PRESS_MS = 500;
// Cancel the long-press once the finger travels this far (Euclidean). Kept BELOW the dnd-kit
// drag-activation distance (kanban 5px, table 6px) so any movement that could start a drag
// cancels the pending menu first — the menu never opens on top of a live drag.
const MOVE_CANCEL_PX = 4;
const MARGIN = 8; // keep the menu at least this far from the viewport edge
const SUB_CLOSE_MS = 140; // grace period so the pointer can cross the gap into a submenu

const isEditable = (t: EventTarget | null) =>
  t instanceof HTMLElement && !!t.closest('input, textarea, select, [contenteditable="true"]');

/**
 * Drives a right-click / long-press context menu for one trigger element: spread
 * `triggerProps` on the element and render `<ContextMenu open x y … />` with the returned
 * state. Right-click opens it at the cursor; on touch a stationary long-press opens it, and
 * any finger movement past a few pixels cancels (it was a scroll or a drag). Text fields keep
 * their native menu. The trailing synthetic tap after a long-press is swallowed so it never
 * falls through to the card underneath.
 */
export function useContextMenu() {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const timer = useRef<number | null>(null);
  const startPt = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);

  const close = useCallback(() => setPos(null), []);

  const onContextMenu = useCallback((e: ReactMouseEvent) => {
    if (isEditable(e.target)) return; // leave the native menu to text fields
    e.preventDefault();
    e.stopPropagation();
    setPos({ x: e.clientX, y: e.clientY });
  }, []);

  const onTouchStart = useCallback(
    (e: ReactTouchEvent) => {
      fired.current = false;
      clearTimer();
      if (e.touches.length !== 1 || isEditable(e.target)) return;
      const t = e.touches[0];
      startPt.current = { x: t.clientX, y: t.clientY };
      e.stopPropagation();
      timer.current = window.setTimeout(() => {
        timer.current = null;
        const p = startPt.current;
        if (p) {
          fired.current = true;
          setPos({ x: p.x, y: p.y });
        }
      }, LONG_PRESS_MS);
    },
    [clearTimer],
  );

  const onTouchMove = useCallback(
    (e: ReactTouchEvent) => {
      const p = startPt.current;
      if (!p || timer.current === null) return;
      const t = e.touches[0];
      if (Math.hypot(t.clientX - p.x, t.clientY - p.y) > MOVE_CANCEL_PX) clearTimer();
    },
    [clearTimer],
  );

  const onTouchEnd = useCallback(
    (e: ReactTouchEvent) => {
      clearTimer();
      startPt.current = null;
      // The long-press already opened the menu; suppress the compatibility mouse/click that
      // would otherwise reach the card (open its drawer) or the backdrop (close the menu).
      if (fired.current) {
        e.preventDefault();
        fired.current = false;
      }
    },
    [clearTimer],
  );

  const triggerProps = {
    onContextMenu,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
  };

  return { open: pos !== null, x: pos?.x ?? 0, y: pos?.y ?? 0, close, triggerProps };
}

/** Move roving focus among the enabled items of `container` for arrow / Home / End. */
function moveFocus(container: HTMLElement, e: ReactKeyboardEvent) {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
  e.preventDefault();
  // A submenu portals to <body> but is a React CHILD of the outer menu, so its keydown also
  // bubbles to the outer menu's handler. Ignore the event unless focus is actually inside THIS
  // container, and stop it so the outer handler doesn't then yank focus out of the submenu.
  if (!container.contains(document.activeElement)) return;
  e.stopPropagation();
  const items = Array.from(
    container.querySelectorAll<HTMLElement>('[role="menuitem"], [role="menuitemradio"]'),
  ).filter((el) => el.getAttribute('aria-disabled') !== 'true');
  if (!items.length) return;
  const idx = items.indexOf(document.activeElement as HTMLElement);
  let next: number;
  if (e.key === 'Home') next = 0;
  else if (e.key === 'End') next = items.length - 1;
  else if (e.key === 'ArrowDown') next = idx < 0 ? 0 : (idx + 1) % items.length;
  else next = idx <= 0 ? items.length - 1 : idx - 1;
  items[next]?.focus({ preventScroll: true });
}

const panelCls =
  'thin-scroll overflow-auto rounded-xl border border-line bg-surface-raised p-1.5 shadow-2xl shadow-black/60';
const rowBase =
  // min-h 48 makes every menu row a full touch target (the menu scrolls if the list is long).
  'flex min-h-[48px] w-full items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-left text-[13px] outline-none transition-colors';

const rowCls = (item: MenuItem) =>
  cn(
    rowBase,
    item.disabled
      ? 'cursor-not-allowed text-fg-ghost'
      : item.danger
        ? 'text-danger hover:bg-danger/10 focus:bg-danger/10'
        : 'text-fg-soft hover:bg-layer hover:text-fg focus:bg-layer focus:text-fg',
  );

function Leading({ item }: { item: MenuItem }) {
  if (!item.icon) return <span className="w-4 shrink-0" aria-hidden="true" />;
  return (
    <span
      className={cn('flex h-4 w-4 shrink-0 items-center justify-center', item.danger ? 'text-danger' : 'text-fg-faint')}
      aria-hidden="true"
    >
      {item.icon}
    </span>
  );
}

function ActionRow({ item, onClose }: { item: MenuItem; onClose: () => void }) {
  return (
    <button
      type="button"
      role={item.checked === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={item.checked === undefined ? undefined : !!item.checked}
      tabIndex={-1}
      disabled={item.disabled}
      aria-disabled={item.disabled || undefined}
      onClick={() => {
        if (item.disabled) return;
        item.onSelect?.();
        onClose();
      }}
      className={rowCls(item)}
    >
      <Leading item={item} />
      <span className="flex-1 truncate">{item.label}</span>
      {item.checked ? (
        <Check className="shrink-0 text-accent" />
      ) : item.shortcut ? (
        <span className="shrink-0 text-[11px] tracking-wide text-fg-faint">{item.shortcut}</span>
      ) : null}
    </button>
  );
}

/** A row that opens a nested menu to its side (flipping / clamping to stay on screen).
 *  Opens on hover (with a close grace period), on click/tap, or on ArrowRight/Enter. */
function SubmenuRow({ item, onClose }: { item: MenuItem; onClose: () => void }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null);
  const rowRef = useRef<HTMLButtonElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), SUB_CLOSE_MS);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  const openSub = useCallback(
    (focusFirst = false) => {
      cancelClose();
      const r = rowRef.current?.getBoundingClientRect();
      if (r) setCoords({ left: r.right, top: r.top }); // refined by the layout effect below
      setOpen(true);
      if (focusFirst) {
        requestAnimationFrame(() =>
          subRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true }),
        );
      }
    },
    [cancelClose],
  );

  // Prefer the right side; flip left if it would overflow, and clamp vertically.
  useLayoutEffect(() => {
    if (!open) return;
    const row = rowRef.current;
    const el = subRef.current;
    if (!row || !el) return;
    const r = row.getBoundingClientRect();
    const { width, height } = el.getBoundingClientRect();
    let left = r.right - 2;
    if (left + width > window.innerWidth - MARGIN) left = Math.max(MARGIN, r.left - width + 2);
    let top = r.top - 6;
    if (top + height > window.innerHeight - MARGIN) top = Math.max(MARGIN, window.innerHeight - height - MARGIN);
    if (top < MARGIN) top = MARGIN;
    setCoords({ left, top });
  }, [open]);

  return (
    <div className="relative" onMouseEnter={() => openSub(false)} onMouseLeave={scheduleClose}>
      <button
        ref={rowRef}
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        tabIndex={-1}
        onClick={() => (open ? setOpen(false) : openSub(false))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openSub(true);
          }
        }}
        className={cn(rowCls(item), open && 'bg-layer text-fg')}
      >
        <Leading item={item} />
        <span className="flex-1 truncate">{item.label}</span>
        <Chevron className="h-3.5 w-3.5 shrink-0 text-fg-faint" />
      </button>
      {open && coords
        ? createPortal(
            <div
              ref={subRef}
              role="menu"
              aria-orientation="vertical"
              style={{ position: 'fixed', left: coords.left, top: coords.top, zIndex: 1001 }}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft' || e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(false);
                  rowRef.current?.focus({ preventScroll: true });
                } else if (e.key === 'Tab') {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                } else {
                  moveFocus(e.currentTarget, e);
                }
              }}
              className={cn(panelCls, 'max-h-[70vh] min-w-[200px]')}
            >
              <MenuList items={item.submenu ?? []} onClose={onClose} />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function MenuList({ items, onClose }: { items: MenuEntry[]; onClose: () => void }) {
  return (
    <>
      {items.map((it, i) => {
        if (it === 'separator') return <div key={`sep-${i}`} role="separator" className="my-1 h-px bg-line" />;
        if ('node' in it) return <div key={`node-${i}`} className="select-none px-1">{it.node}</div>;
        if (it.submenu?.length) return <SubmenuRow key={`${it.label}-${i}`} item={it} onClose={onClose} />;
        return <ActionRow key={`${it.label}-${i}`} item={it} onClose={onClose} />;
      })}
    </>
  );
}

/**
 * A floating menu anchored at a viewport point (the cursor / long-press). Renders into a
 * body portal above a full-screen backdrop that closes it on outside click, right-click,
 * scroll, resize or Escape. Flips and clamps so it never spills off screen.
 */
export function ContextMenu({
  open,
  x,
  y,
  items,
  onClose,
}: {
  open: boolean;
  x: number;
  y: number;
  items: MenuEntry[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    if (!open) return;
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + width > window.innerWidth - MARGIN) left = Math.max(MARGIN, x - width);
    if (left < MARGIN) left = MARGIN;
    if (top + height > window.innerHeight - MARGIN) top = Math.max(MARGIN, window.innerHeight - height - MARGIN);
    if (top < MARGIN) top = MARGIN;
    setCoords({ left, top });
  }, [open, x, y]);

  useEffect(() => {
    if (!open) return undefined;
    // Remember the trigger (the focused card/row) so focus returns there when the menu closes,
    // instead of falling to <body> — important for keyboard users who opened it via Shift+F10.
    const restoreTo = document.activeElement as HTMLElement | null;
    // Focus the panel itself (not the first item) so Escape / arrow keys work without
    // pre-highlighting a row like a hover; ArrowDown from here lands on the first item.
    ref.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onScroll = (e: Event) => {
      // A scroll inside the main panel OR any open submenu (both carry role="menu") is fine.
      if ((e.target as HTMLElement | null)?.closest?.('[role="menu"]')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', onScroll, true);
      restoreTo?.focus?.({ preventScroll: true });
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000]"
      onMouseDown={onClose}
      onContextMenu={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div
        ref={ref}
        role="menu"
        aria-orientation="vertical"
        tabIndex={-1}
        style={{ position: 'fixed', left: coords.left, top: coords.top }}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            // Don't let Tab move focus behind the open menu — dismiss it (focus returns to the trigger).
            e.preventDefault();
            onClose();
            return;
          }
          moveFocus(e.currentTarget, e);
        }}
        className={cn(panelCls, 'max-h-[80vh] min-w-[224px] outline-none')}
      >
        <MenuList items={items} onClose={onClose} />
      </div>
    </div>,
    document.body,
  );
}

export default ContextMenu;
